/**
 * «Andamenti» della scheda atleta: un solo periodo (settimane intere) e un solo
 * raggruppamento per tutto quello che c'e' sotto (D32). Prima erano due coppie
 * di selettori indipendenti, una nel tab dello sport e una negli andamenti, e
 * passando da una card al dettaglio il periodo scelto si perdeva.
 *
 * Sotto il periodo, una scelta di cosa guardare:
 *   - uno sport dell'atleta: gli esercizi del profilo prestativo attivo (D26),
 *     ognuno una card da cui si apre l'andamento completo;
 *   - «Tutto il lavoro»: il selettore libero di un movimento o di una famiglia.
 * Aprendo una card si scende al dettaglio del movimento, con «← Esercizi del
 * profilo» per risalire.
 */
import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { formatSportLabel } from "../../lib/utils";
import { TREND_BUCKETS, TREND_WEEKS } from "../../lib/monitoringUtils";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { ExerciseTrendPanel } from "./ExerciseTrendPanel";
import { SportProfileTrends } from "./SportProfileTrends";

export const BUCKET_LABEL_KEYS = {
	week: "monBucketWeek",
	session: "monBucketSession",
	month: "monBucketMonth",
	program: "monBucketProgram",
};

const ALL_WORK = "__all__";

function ChoiceChip({ active, onClick, children, testId }) {
	return (
		<button
			type="button"
			aria-pressed={active}
			onClick={onClick}
			data-testid={testId}
			className={`shrink-0 rounded-full border px-3 h-9 text-sm transition-colors ${
				active
					? "border-volt/60 bg-volt/15 text-foreground font-medium"
					: "border-border text-muted-foreground hover:text-foreground"
			}`}
		>
			{children}
		</button>
	);
}

export function AthleteTrends({ athleteId, sports, sportLabels, today, programId, t, language }) {
	const [weeks, setWeeks] = useState(12);
	const [bucket, setBucket] = useState("week");
	// Cosa si guarda: uno sport (le card del profilo) o tutto il lavoro.
	const [scope, setScope] = useState(null);
	// Il movimento aperto; `fromSport` dice se si e' arrivati da una card.
	const [movement, setMovement] = useState(null);
	const [fromSport, setFromSport] = useState(null);

	// Senza una scelta del coach: il primo sport, o tutto il lavoro se l'atleta non ne ha.
	const activeScope = scope || (sports.length ? sports[0] : ALL_WORK);
	const showingSport = activeScope !== ALL_WORK && !movement;

	// Cambiando atleta si riparte dall'inizio.
	useEffect(() => {
		setScope(null);
		setMovement(null);
		setFromSport(null);
	}, [athleteId]);

	const chooseScope = (next) => {
		setScope(next);
		setMovement(next === ALL_WORK ? "all" : null);
		setFromSport(null);
	};

	const openMovement = (key) => {
		setFromSport(activeScope);
		setMovement(key);
	};

	const sportLabel = (slug) => formatSportLabel(slug, sportLabels);

	return (
		<div className="space-y-4" data-testid="athlete-trends">
			<div className="flex flex-wrap items-end justify-between gap-3">
				{sports.length ? (
					<div className="flex gap-1.5 overflow-x-auto no-scrollbar max-w-full" role="group" aria-label={t("monTrendsWhat")}>
						{sports.map((slug) => (
							<ChoiceChip
								key={slug}
								active={activeScope === slug && (showingSport || fromSport === slug)}
								onClick={() => chooseScope(slug)}
								testId={`sport-tab-${slug}`}
							>
								{sportLabel(slug)}
							</ChoiceChip>
						))}
						<ChoiceChip
							active={activeScope === ALL_WORK}
							onClick={() => chooseScope(ALL_WORK)}
							testId="trends-all-work"
						>
							{t("monTrendsAllWork")}
						</ChoiceChip>
					</div>
				) : (
					<p className="text-xs text-muted-foreground max-w-md">{t("monSportNoSports")}</p>
				)}
				<div className="grid grid-cols-2 gap-2 w-full sm:flex sm:w-auto">
					<Select value={String(weeks)} onValueChange={(value) => setWeeks(Number(value))}>
						<SelectTrigger className="rounded-sm w-full sm:w-[180px]" aria-label={t("monPeriod")}>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{TREND_WEEKS.map((value) => (
								<SelectItem key={value} value={String(value)}>
									{t("monLastWeeks")} {value}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Select value={bucket} onValueChange={setBucket}>
						<SelectTrigger className="rounded-sm w-full sm:w-[160px]" aria-label={t("monBucket")}>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{TREND_BUCKETS.map((value) => (
								<SelectItem key={value} value={value}>
									{t(BUCKET_LABEL_KEYS[value])}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>

			{showingSport ? (
				<SportProfileTrends
					athleteId={athleteId}
					sport={activeScope}
					sportLabel={sportLabel(activeScope)}
					weeks={weeks}
					bucket={bucket}
					today={today}
					programId={programId}
					onOpenTrend={openMovement}
					onOpenAllWork={() => chooseScope(ALL_WORK)}
					t={t}
					language={language}
				/>
			) : (
				<div className="space-y-3">
					{fromSport ? (
						<button
							type="button"
							onClick={() => {
								setMovement(null);
								setFromSport(null);
							}}
							className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
							data-testid="trends-back-to-profile"
						>
							<ArrowLeft className="w-4 h-4" aria-hidden="true" />
							{t("monTrendsBackToProfile")} · {sportLabel(fromSport)}
						</button>
					) : null}
					<ExerciseTrendPanel
						athleteId={athleteId}
						weeks={weeks}
						bucket={bucket}
						movement={movement || "all"}
						onMovementChange={setMovement}
						today={today}
						programId={programId}
						t={t}
						language={language}
					/>
				</div>
			)}
		</div>
	);
}
